import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  bootstrapCustomerSession,
  clearCustomerSession,
  loginCustomerSession,
  logoutCustomerSession,
  subscribeCustomerSessionExpired,
  type CustomerAccount,
  type CustomerLoginInput,
} from '../api/customerSession';

/**
 * 客户会话 Context（Issue #198）。
 *
 * 单一职责：把 customerSession 模块的会话生命周期暴露给 UI。
 *   - 不包含权限矩阵；权限完全由后端在 /me/* 接口里执行（前端只看可见数据）。
 *   - 不在 Context 中缓存任何 access/refresh token；token 由 sessionStore 管理。
 *   - signOut 后必须清空 React state，避免短暂残留 customer 信息。
 */
export interface SessionState {
  account: CustomerAccount | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  signIn: (input: CustomerLoginInput) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const refreshed = await bootstrapCustomerSession();
        if (!refreshed) {
          clearCustomerSession();
          if (!cancelled) setAccount(null);
        }
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeCustomerSessionExpired(() => {
      setAccount(null);
    });
  }, []);

  const signIn = useCallback(async (input: CustomerLoginInput) => {
    const session = await loginCustomerSession(input);
    setAccount(session.account);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutCustomerSession();
    } finally {
      setAccount(null);
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      account,
      isAuthenticated: account !== null,
      isInitializing,
      signIn,
      signOut,
    }),
    [account, isInitializing, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (ctx === undefined) {
    throw new Error('useSession must be used within <SessionProvider>');
  }
  return ctx;
}
